/**
 * Rolling throughput model for the downloaded-report adoption / reparse pass.
 * The restore page sits in front of the user for the whole pass, so it can
 * answer "how much longer?" the way long-running installs do:
 * from a blend of THIS run's measured throughput and the throughput of the
 * runs recorded before it. Everything here is plain data plumbing — no React,
 * no Word — so jest covers it without an Office host.
 */

/** localStorage key holding the last few completed runs (JSON array). */
export const ADOPTION_PARSE_STATS_KEY = "jenly_adoption_parse_stats_v1";

/**
 * Returns a platform-partitioned storage key. Office on the Web (OfficeOnline)
 * and desktop Word (Mac/Win) exhibit different Office.js dispatch latency,
 * so historical throughput is segmented by platform to avoid skewed estimates.
 */
export const getAdoptionParseStatsKey = (platform?: string | null): string => {
  if (!platform) return ADOPTION_PARSE_STATS_KEY;
  const normalized = platform.toLowerCase().trim();
  if (normalized.includes("web") || normalized.includes("online")) {
    return `${ADOPTION_PARSE_STATS_KEY}_web`;
  }
  if (
    normalized.includes("mac") ||
    normalized.includes("win") ||
    normalized.includes("desktop") ||
    normalized === "pc"
  ) {
    return `${ADOPTION_PARSE_STATS_KEY}_desktop`;
  }
  return `${ADOPTION_PARSE_STATS_KEY}_${normalized}`;
};
/** Runs kept in history; the oldest is dropped once the cap is reached. */
export const ADOPTION_PARSE_STATS_CAP = 10;
/** A run whose own throughput leaves the median x/÷ this band is an outlier. */
export const ADOPTION_PARSE_OUTLIER_FACTOR = 2;
/** EMA weight given to the newest throughput sample (0-1). */
const EMA_ALPHA = 0.35;
/** The estimate is returned as central ± this fraction. */
const ETA_RANGE_SPREAD = 0.25;
/** Below one minute the range is noise — collapsed to "under a minute". */
export const ETA_MINUTE_MS = 60000;

/** One finished pass, persisted so a cold start can estimate from it. */
export interface AdoptionParseRun {
  unitsTotal: number;
  durationMs: number;
  /** Completion time; informational only, never read back for math. */
  at?: number;
}

/** Inclusive remaining-time window in milliseconds. */
export type AdoptionEtaRange = [lowMs: number, highMs: number];

const defaultStorage = (): Storage | null => {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch (_) {
    return null;
  }
};

const isRun = (value: unknown): value is AdoptionParseRun => {
  if (!value || typeof value !== "object") return false;
  const run = value as Partial<AdoptionParseRun>;
  return typeof run.unitsTotal === "number" && run.unitsTotal > 0 &&
    typeof run.durationMs === "number" && run.durationMs > 0;
};

/** Parse history JSON, tolerating anything missing or corrupt. */
export const parseAdoptionParseStats = (raw: string | null): AdoptionParseRun[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRun) : [];
  } catch (_) {
    return [];
  }
};

export const loadAdoptionParseStats = (
  storage: Storage | null = defaultStorage(),
  key: string = ADOPTION_PARSE_STATS_KEY,
): AdoptionParseRun[] =>
  parseAdoptionParseStats(storage ? storage.getItem(key) : null);

const unitsPerMs = (run: AdoptionParseRun): number => run.unitsTotal / run.durationMs;

const median = (values: number[]): number => {
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/**
 * Drop runs whose own throughput sits outside median x/÷ the outlier factor,
 * so one anomalous pass (a host hiccup, a background save) cannot skew the
 * rate every future cold start estimates from. Symmetric on purpose: a run
 * that was suspiciously slow is just as unrepresentative as a fast one.
 */
export const pruneAdoptionParseOutliers = (runs: AdoptionParseRun[]): AdoptionParseRun[] => {
  if (runs.length === 0) return [];
  const middle = median(runs.map(unitsPerMs));
  if (!(middle > 0)) return runs.slice();
  const floor = middle / ADOPTION_PARSE_OUTLIER_FACTOR;
  const ceiling = middle * ADOPTION_PARSE_OUTLIER_FACTOR;
  return runs.filter((run) => {
    const rate = unitsPerMs(run);
    return rate >= floor && rate <= ceiling;
  });
};

/**
 * Append a finished run to history: prune outliers, keep only the newest
 * ADOPTION_PARSE_STATS_CAP runs, and persist. Returns the stored history.
 */
export const recordAdoptionParseRun = (
  run: AdoptionParseRun,
  storage: Storage | null = defaultStorage(),
  key: string = ADOPTION_PARSE_STATS_KEY,
): AdoptionParseRun[] => {
  const existing = loadAdoptionParseStats(storage, key);
  if (!isRun(run) || !storage) return existing;
  const history = pruneAdoptionParseOutliers(existing.concat([run])).slice(-ADOPTION_PARSE_STATS_CAP);
  try {
    storage.setItem(key, JSON.stringify(history));
  } catch (_) {
    // Private-browsing storage quotas must never fail the pass itself.
  }
  return history;
};

/** Aggregate throughput across history: total units over total milliseconds. */
export const adoptionHistoryUnitsPerMs = (runs: AdoptionParseRun[]): number | null => {
  let units = 0;
  let ms = 0;
  for (const run of runs) {
    units += run.unitsTotal;
    ms += run.durationMs;
  }
  return ms > 0 ? units / ms : null;
};

/** Units-per-millisecond EMA fed by cumulative progress reports. */
export interface AdoptionThroughputTracker {
  observe: (unitsDone: number, elapsedMs: number) => void;
  unitsPerMs: () => number | null;
};

export const createAdoptionThroughputTracker = (alpha: number = EMA_ALPHA): AdoptionThroughputTracker => {
  let ema: number | null = null;
  let lastUnits = 0;
  let lastMs = 0;
  return {
    observe(unitsDone: number, elapsedMs: number) {
      // Not primed yet (or the count went backwards — a retried pass starts a
      // new run): seed from the whole-run average, which is all we know.
      if (ema === null || unitsDone < lastUnits) {
        if (unitsDone > 0 && elapsedMs > 0) {
          ema = unitsDone / elapsedMs;
          lastUnits = unitsDone;
          lastMs = elapsedMs;
        }
        return;
      }
      const deltaUnits = unitsDone - lastUnits;
      const deltaMs = elapsedMs - lastMs;
      if (deltaUnits > 0 && deltaMs > 0) {
        ema = alpha * (deltaUnits / deltaMs) + (1 - alpha) * ema;
      }
      lastUnits = unitsDone;
      lastMs = elapsedMs;
    },
    unitsPerMs: () => ema,
  };
};

/** Throughput inputs for an estimate; absent values are simply not blended. */
export interface AdoptionEstimateBasis {
  /** Live EMA throughput for this run, once at least one unit has finished. */
  currentUnitsPerMs?: number | null;
  /** Aggregate throughput of past runs, when any were recorded. */
  historicalUnitsPerMs?: number | null;
}

/**
 * Remaining-time window for a pass. Falls back to this run's whole average
 * when no EMA has been fed, blends it with historical throughput when history
 * exists (history leads early — a one-unit sample is noisy; the live rate
 * leads once this run has covered real ground), and returns null when there is
 * no basis at all: no units done, no history, or no known total.
 */
export const estimateAdoptionRemainingMs = (
  unitsDone: number,
  unitsTotal: number,
  elapsedMs: number,
  basis: AdoptionEstimateBasis = {},
): AdoptionEtaRange | null => {
  if (!(unitsTotal > 0)) return null;
  const remaining = unitsTotal - Math.max(0, unitsDone);
  if (remaining <= 0) return [0, 0];
  const current = basis.currentUnitsPerMs !== undefined && basis.currentUnitsPerMs !== null
    ? basis.currentUnitsPerMs
    : unitsDone > 0 && elapsedMs > 0 ? unitsDone / elapsedMs : null;
  const historical = basis.historicalUnitsPerMs !== undefined && basis.historicalUnitsPerMs !== null
    ? basis.historicalUnitsPerMs
    : null;
  let throughput: number | null;
  if (current !== null && historical !== null) {
    const liveWeight = Math.min(1, Math.max(0, unitsDone / unitsTotal));
    throughput = liveWeight * current + (1 - liveWeight) * historical;
  } else {
    throughput = current !== null ? current : historical;
  }
  if (throughput === null || !(throughput > 0)) return null;
  const centralMs = remaining / throughput;
  const low = Math.max(0, Math.round(centralMs * (1 - ETA_RANGE_SPREAD)));
  const high = Math.max(low + 1, Math.round(centralMs * (1 + ETA_RANGE_SPREAD)));
  return [low, high];
};

/** Human text for an ETA window, formatted following standard OS progress conventions. */
export const formatAdoptionEta = (range: AdoptionEtaRange | null): string | null => {
  if (!range) return null;
  if (range[1] < ETA_MINUTE_MS) return "Less than 1 minute remaining";
  const lowMinutes = Math.max(1, Math.floor(range[0] / ETA_MINUTE_MS));
  const highMinutes = Math.max(lowMinutes, Math.ceil(range[1] / ETA_MINUTE_MS));
  if (lowMinutes === highMinutes) {
    return `About ${lowMinutes} ${lowMinutes === 1 ? "minute" : "minutes"} remaining`;
  }
  return `About ${lowMinutes}–${highMinutes} minutes remaining`;
};

/** Stateful facade the page drives from its progress prop. */
export interface AdoptionEstimator {
  /** Feed one progress report: cumulative units done, ms since the pass began. */
  observe: (unitsDone: number, elapsedMs: number) => void;
  /** Remaining-time window, or null while nothing is measurable. */
  estimateRemainingMs: (unitsDone: number, unitsTotal: number, elapsedMs: number) => AdoptionEtaRange | null;
  /** Persist a finished run so the next cold start can estimate from it. */
  complete: (unitsTotal: number, durationMs: number) => void;
}

export interface AdoptionEstimatorOptions {
  storage?: Storage | null;
  alpha?: number;
  platform?: string | null;
}

export const createAdoptionEstimator = (
  options: AdoptionEstimatorOptions = {},
): AdoptionEstimator => {
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  const tracker = createAdoptionThroughputTracker(options.alpha);
  const statsKey = getAdoptionParseStatsKey(options.platform);
  let lastEstimatedCentralMs: number | null = null;
  let lastEstimateUnits = -1;

  return {
    observe: (unitsDone, elapsedMs) => tracker.observe(unitsDone, elapsedMs),
    estimateRemainingMs: (unitsDone, unitsTotal, elapsedMs) => {
      const stats = loadAdoptionParseStats(storage, statsKey);
      const historicalRuns = stats.length > 0
        ? stats
        : (statsKey !== ADOPTION_PARSE_STATS_KEY ? loadAdoptionParseStats(storage, ADOPTION_PARSE_STATS_KEY) : []);

      const rawRange = estimateAdoptionRemainingMs(unitsDone, unitsTotal, elapsedMs, {
        currentUnitsPerMs: tracker.unitsPerMs(),
        historicalUnitsPerMs: adoptionHistoryUnitsPerMs(historicalRuns),
      });

      if (!rawRange) return null;
      if (rawRange[0] === 0 && rawRange[1] === 0) {
        lastEstimatedCentralMs = 0;
        return [0, 0];
      }

      if (unitsDone < lastEstimateUnits) {
        lastEstimatedCentralMs = null;
      }
      lastEstimateUnits = unitsDone;

      let centralMs = (rawRange[0] + rawRange[1]) / 2;
      if (lastEstimatedCentralMs !== null && centralMs > lastEstimatedCentralMs) {
        // Damping: limits sudden upward jumps when transitioning between stages
        // (e.g. from fast bookmark checks to heavier section styling). Downward
        // adjustments remain instantaneous.
        const maxUpward = Math.max(lastEstimatedCentralMs * 1.2, lastEstimatedCentralMs + 10000);
        centralMs = Math.min(centralMs, maxUpward);
      }
      lastEstimatedCentralMs = centralMs;

      const low = Math.max(0, Math.round(centralMs * (1 - ETA_RANGE_SPREAD)));
      const high = Math.max(low + 1, Math.round(centralMs * (1 + ETA_RANGE_SPREAD)));
      return [low, high];
    },
    complete: (unitsTotal, durationMs) => {
      recordAdoptionParseRun({ unitsTotal, durationMs, at: Date.now() }, storage, statsKey);
    },
  };
};
